import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Modal } from 'antd';

import { logoFontsReady, logoSVG, mountFootArt } from './whales';
import { usePalette } from '../../theme/PaletteContext';


export const LAB_PATHS = ['/whalesLab', '/labMemberPage'];

// a link to a section of the WHALES Lab page: scrolls there, or opens the lab page at that section
const SectionLink = ({ id, className, label, children }: { id: string, className?: string, label?: string, children?: React.ReactNode }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const onClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!LAB_PATHS.includes(location.pathname)) navigate(id === 'top' ? '/whalesLab' : `/whalesLab#${id}`);
    else if (id === 'top') window.scrollTo({ top: 0, behavior: 'smooth' });
    else document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };
  return <a href={`/whalesLab#${id}`} className={className} aria-label={label} onClick={onClick}>{children}</a>;
};

const ext = { target: '_blank', rel: 'noreferrer' };
const EMAIL = 'yifang.wang@fsu.edu';

// the site footer (every page): the WHALES wordmark, three link columns, and a halftone 'sea of streamlines' rising from the bottom
const LabFooter: React.FC<{ night?: boolean }> = ({ night = false }) => {
  const artRef = useRef<HTMLCanvasElement>(null);
  const [copied, setCopied] = useState(false);
  // the email icon copies the address (as the Work with me page's email links do)
  const copyEmail = async (e: React.MouseEvent) => {
    e.preventDefault();
    try {
      await navigator.clipboard.writeText(EMAIL);
      setCopied(true);
    } catch (err) {
      Modal.error({ title: 'Copy Failed', content: 'Failed to copy email to clipboard.', centered: true });
    }
  };
  const { theme } = usePalette();
  const tone = night || theme.dark ? 'dark' : 'light'; // night: on a black page (the data-art page)
  const [logo, setLogo] = useState(() => logoSVG('markword', tone));

  // redrawn whenever the palette changes (the mark engine and --foot-bg follow it)
  useEffect(() => {
    let alive = true;
    setLogo(logoSVG('markword', tone));
    // the word WHALES is measured from the web font: redraw it once the font is in
    logoFontsReady().then(() => { if (alive) setLogo(logoSVG('markword', tone)); });
    const stop = artRef.current ? mountFootArt(artRef.current) : () => { };
    return () => { alive = false; stop(); };
  }, [theme, tone]);

  return (
    <footer className={`foot${night ? ' is-night' : ''}`}>
      <div className='wrap foot-in'>
        <div className='f-brand'>
          <SectionLink id='top' className='lg' label='WHALES Lab — back to top'><span dangerouslySetInnerHTML={{ __html: logo }} /></SectionLink>
          <p className='f-tag'><b>W</b>here <b>H</b>uman and <b>A</b>I co<b>L</b>laborate for <b>E</b>xploring <b>S</b>cience · <b>S</b>ociety</p>
          <p className='f-addr'>Department of Computer Science<br />Florida State University · Tallahassee, FL 32306</p>
          <div className='f-soc'>
            <a href={`mailto:${EMAIL}`} aria-label='Copy email address' onClick={copyEmail}><svg viewBox='0 0 24 24' width='17' height='17' fill='none' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'><path d='M4 6h16v12H4z' /><path d='M4 7l8 6 8-6' /></svg></a>
            <a href='https://scholar.google.com/citations?user=3qWVVUwAAAAJ&hl=en' aria-label='Google Scholar' {...ext}><svg viewBox='0 0 24 24' width='17' height='17' fill='none' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'><path d='M12 4 2 9l10 5 10-5z' /><path d='M6 11.5V16c2 2 10 2 12 0v-4.5' /></svg></a>
            <a href='https://www.linkedin.com/in/yifanginhz/' aria-label='LinkedIn' {...ext}><svg viewBox='0 0 24 24' width='17' height='17' fill='none' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'><rect x='3.5' y='3.5' width='17' height='17' rx='3' /><path d='M8 10.5V16M8 7.8v.01M11.5 16v-5.5M11.5 13a2.5 2.5 0 0 1 5 0v3' /></svg></a>
            <a href='https://x.com/yifanginhz' aria-label='X / Twitter' {...ext}><svg viewBox='0 0 24 24' width='17' height='17' fill='none' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'><path d='M4 4l16 16M20 4 4 20' /></svg></a>
            <a href='https://bsky.app/profile/yifangwang.bsky.social' aria-label='Bluesky' {...ext}><svg viewBox='0 0 24 24' width='17' height='17' fill='none' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'><path d='M12 11.2C10.6 8.4 6.9 4.5 4.8 4.5c-1 0-1.3.9-1.3 1.8 0 .9.5 4.6 1 5.3.9 1.3 2.8 1.6 4.7 1.2-2.7.5-4.8 1.8-2.6 4.4 2.6 2.9 4.5-.6 5.4-3 .9 2.4 2.8 5.9 5.4 3 2.2-2.6.1-3.9-2.6-4.4 1.9.4 3.8.1 4.7-1.2.5-.7 1-4.4 1-5.3 0-.9-.3-1.8-1.3-1.8-2.1 0-5.8 3.9-7.2 6.7z' /></svg></a>
          </div>
        </div>
        <nav className='f-col' aria-label='Lab'>
          <h4>Lab</h4>
          <ul>
            <li><SectionLink id='about'>About</SectionLink></li>
            <li><SectionLink id='publications'>Publications</SectionLink></li>
            <li><SectionLink id='people'>People</SectionLink></li>
            <li><Link to='/recruitingPage'>Join us</Link></li>
          </ul>
        </nav>
        <nav className='f-col' aria-label='Research'>
          <h4>Research</h4>
          <ul>
            <li><a href='https://llm-native-figure.com/' {...ext}>LLM-Native Figures</a></li>
            <li><a href='https://sciscigpt.com/' {...ext}>SciSciGPT</a></li>
            <li><a href='https://kellogg-cssi.github.io/InnovationInsights/' {...ext}>InnovationInsights</a></li>
            <li><Link to='/dataArtPage'>Data-driven art</Link></li>
          </ul>
        </nav>
        <nav className='f-col' aria-label='Contact'>
          <h4>Contact</h4>
          <ul>
            <li><a href='mailto:yifang.wang@fsu.edu'>yifang.wang@fsu.edu</a></li>
            <li><Link to='/'>Yifang Wang ↗</Link></li>
            <li><a href='https://forms.gle/mFEhhzz1g8dG4MnNA' {...ext}>Application form ↗</a></li>
            <li><a href='https://www.cs.fsu.edu/' {...ext}>FSU Computer Science ↗</a></li>
          </ul>
        </nav>
        <p className='f-motto'><span lang='zh-CN'>以一灯传诸灯 终至万灯皆明</span><span>© 2017–2026 Yifang Wang</span></p>
      </div>
      <canvas className='f-art' ref={artRef} aria-hidden='true' />
      <Modal title={null} open={copied} onOk={() => setCopied(false)} onCancel={() => setCopied(false)} closable={false} footer={null}
        className='email-copy-modal' style={{ top: 50 }} width={300} transitionName=''>
        <div className='modal-content'><div className='modal-message'>Copied: {EMAIL}</div></div>
      </Modal>
    </footer>
  );
};

export default LabFooter;
